import type { TFunction } from 'i18next';

import type { ActiveFilterChip } from '@/features/header-filters';
import type { SelectOption } from '@/shared/ui';

import type { ReceiveUrlState } from './types';

interface BuildReceiveFilterChipsArgs {
  state: ReceiveUrlState;
  t: TFunction;
  // id → name options for the organization filter
  organizationOptions: SelectOption[];
  onRemove: (key: string) => void;
}

/**
 * Derives the removable active-filter chips from the committed receive filter
 * state (search excluded — it lives in the header search box). Mirrors the
 * `activeFiltersCount` accounting in use-receive-filters so the chip count
 * matches the header badge. Extracted + pure so it can be unit-tested
 * independently of the hook (same layout as the consignments page).
 */
export function buildReceiveFilterChips({
  state,
  organizationOptions,
  onRemove
}: BuildReceiveFilterChipsArgs): ActiveFilterChip[] {
  const chips: ActiveFilterChip[] = [];

  if (state.consignmentNumber) {
    chips.push({
      key: 'consignmentNumber',
      label: state.consignmentNumber,
      onRemove: () => onRemove('consignmentNumber')
    });
  }

  if (state.organization) {
    const label = organizationOptions.find((o) => o.value === state.organization)?.label ?? state.organization;
    chips.push({ key: 'organization', label, onRemove: () => onRemove('organization') });
  }

  if (state.originLocation) {
    chips.push({
      key: 'originLocation',
      label: state.originLocation,
      onRemove: () => onRemove('originLocation')
    });
  }

  if (state.shipmentIdentification) {
    chips.push({
      key: 'shipmentIdentification',
      label: state.shipmentIdentification,
      onRemove: () => onRemove('shipmentIdentification')
    });
  }

  return chips;
}
